import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { authApi, inr } from '@/lib/erpApi';
import {
  Building2, Store, UserCircle2, Home, IndianRupee, TrendingUp,
  CalendarClock, AlertTriangle, Loader2,
} from 'lucide-react';

const stat = (label, value, sub, Icon, tone = 'orange') => ({
  label, value, sub, Icon, tone,
});

const toneCls = {
  orange: 'from-orange-50 to-amber-50 text-brand-orange',
  green: 'from-emerald-50 to-teal-50 text-emerald-700',
  blue: 'from-sky-50 to-cyan-50 text-sky-700',
  rose: 'from-rose-50 to-pink-50 text-rose-700',
  slate: 'from-gray-50 to-slate-50 text-slate-700',
};

export default function ERPDashboard() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authApi(token).get('/erp/dashboard').then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-brand-orange" /></div>;
  if (!data) return null;

  const cards = [
    stat('Today\'s Collection', inr(data.money.today_collection), 'Rent + EMI + Electricity', IndianRupee, 'green'),
    stat('Today\'s Due', inr(data.money.today_due), 'Bills expected today', CalendarClock, 'orange'),
    stat('Monthly Collection', inr(data.money.monthly_collection), 'Current month', TrendingUp, 'blue'),
    stat('Outstanding', inr(data.money.outstanding), 'Across all customers', AlertTriangle, 'rose'),
  ];

  const rental = [
    stat('Buildings', data.rental.buildings, 'Total buildings', Building2, 'orange'),
    stat('Units', data.rental.units_total, `${data.rental.units_occupied} occupied · ${data.rental.units_vacant} vacant`, Store, 'blue'),
    stat('Occupancy', `${data.rental.occupancy_pct}%`, 'Occupied ratio', Home, 'green'),
    stat('Tenants', data.rental.tenants, 'Active tenants', UserCircle2, 'slate'),
  ];

  const emi = [
    stat('EMI Customers', data.emi.customers, 'Active customers', UserCircle2, 'blue'),
    stat('EMI Plans', data.emi.active_plans, 'Active plans', IndianRupee, 'green'),
  ];

  return (
    <div className="space-y-8" data-testid="erp-dashboard">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900">ERP Dashboard</h1>
        <p className="text-gray-500 text-sm mt-1">Live operations snapshot · money numbers activate in Phase 2 (billing).</p>
      </div>

      <section>
        <h2 className="font-heading text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">Money</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c) => <StatCard key={c.label} {...c} />)}
        </div>
      </section>

      <section>
        <h2 className="font-heading text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">Rental</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {rental.map((c) => <StatCard key={c.label} {...c} />)}
        </div>
      </section>

      <section>
        <h2 className="font-heading text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">EMI</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {emi.map((c) => <StatCard key={c.label} {...c} />)}
        </div>
      </section>

      <section>
        <h2 className="font-heading text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">Quick Start</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/dashboard/erp/buildings" className="p-5 bg-white border rounded-xl hover:shadow-md transition-all hover:-translate-y-0.5">
            <Building2 className="h-6 w-6 text-brand-orange mb-2" />
            <p className="font-semibold text-gray-900">Add a Building</p>
            <p className="text-xs text-gray-500 mt-1">Start with a property or complex.</p>
          </Link>
          <Link to="/dashboard/erp/tenants" className="p-5 bg-white border rounded-xl hover:shadow-md transition-all hover:-translate-y-0.5">
            <UserCircle2 className="h-6 w-6 text-brand-orange mb-2" />
            <p className="font-semibold text-gray-900">Add a Tenant</p>
            <p className="text-xs text-gray-500 mt-1">Capture KYC + contacts + docs.</p>
          </Link>
          <Link to="/dashboard/erp/emi/customers" className="p-5 bg-white border rounded-xl hover:shadow-md transition-all hover:-translate-y-0.5">
            <IndianRupee className="h-6 w-6 text-brand-orange mb-2" />
            <p className="font-semibold text-gray-900">Add EMI Customer</p>
            <p className="text-xs text-gray-500 mt-1">Book a plot with an EMI schedule.</p>
          </Link>
        </div>
      </section>
    </div>
  );
}

function StatCard({ label, value, sub, Icon, tone }) {
  return (
    <Card className="overflow-hidden">
      <CardContent className={`p-4 bg-gradient-to-br ${toneCls[tone]}`}>
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wider opacity-80">{label}</p>
            <p className="font-heading text-2xl font-bold mt-1 truncate">{value}</p>
            {sub && <p className="text-xs opacity-70 mt-1">{sub}</p>}
          </div>
          <Icon className="h-8 w-8 opacity-40 shrink-0" />
        </div>
      </CardContent>
    </Card>
  );
}
